import { useMemo } from 'react';
import { musikkAPI } from '@/services/api';
import { generateSongPDF } from '@/lib/generateSongPDF';
import { tolkSang } from '@/lib/sang/tolkSang';
import { tolkGrunntone } from '@/lib/sang/toneart';
import { lagHode, visSang } from '@/lib/sang/visSang';
import { useTranspositions } from './transpositions-tab/hooks/useTranspositions';
import { useTranspositionForm } from './transpositions-tab/hooks/useTranspositionForm';
import { useTranspositionSettings } from './transpositions-tab/hooks/useTranspositionSettings';
import { useMessages } from './transpositions-tab/hooks/useMessages';
import { MessageToast } from './transpositions-tab/components/MessageToast';
import { TranspositionToolCard } from './transpositions-tab/components/TranspositionToolCard';
import { SavedTranspositionsCard } from './transpositions-tab/components/SavedTranspositionsCard';
import type { Musikk } from '@/services/api';

interface Props {
  musikk: Musikk | null;
}

export default function TranspositionsTab({ musikk }: Props) {
  const messages = useMessages();
  const settings = useTranspositionSettings();
  const transpositions = useTranspositions({ musikkId: musikk?.id });
  const form = useTranspositionForm({
    musikk,
    lagrede: transpositions.savedTranspositions,
    onSuccess: messages.setSuccessMessage,
    onError: messages.setErrorMessage,
    onReload: transpositions.loadTransponeringer,
  });

  const tekstOgAkkorder = musikk?.tekst_og_akkorder || '';
  const semitoner = parseInt(form.transposeBy);
  const grunntone = musikk?.grunntone;
  const toneartType = musikk?.toneart_type;
  const toneart = useMemo(() => tolkGrunntone(grunntone, toneartType), [grunntone, toneartType]);
  const dokument = useMemo(() => tolkSang(tekstOgAkkorder), [tekstOgAkkorder]);
  const visValg = useMemo(() => ({ toneart, semitoner, visning: form.visning }), [toneart, semitoner, form.visning]);
  const linjer = useMemo(() => visSang(dokument, visValg), [dokument, visValg]);
  const hode = lagHode(dokument, { tittel: musikk?.tittel ?? '', artist: musikk?.artist }, visValg);

  const handleDeleteTransposition = async (id: number) => {
    if (!window.confirm('Er du sikker på at du vil slette denne transponeringen?')) {
      return;
    }

    try {
      await musikkAPI.deleteTransponering(id);
      messages.setSuccessMessage('Transponering slettet');
      await transpositions.loadTransponeringer();
    } catch (err: any) {
      console.error('Kunne ikke slette transponering:', err);
      messages.setErrorMessage(err.response?.data?.error || 'Kunne ikke slette transponering');
    }
  };

  const handleDownloadPDF = () => {
    if (!musikk || !tekstOgAkkorder) {
      messages.setErrorMessage('Ingen tekst å laste ned');
      return;
    }

    try {
      generateSongPDF({
        title: musikk.tittel,
        artist: musikk.artist,
        tekstOgAkkorder,
        semitones: semitoner,
        originalGrunntone: musikk.grunntone,
        originalToneartType: musikk.toneart_type,
        fontSize: parseInt(settings.fontSize),
        chordFontSize: settings.chordFontSize,
        chordColor: settings.chordColor,
        columns: settings.columns === '2' ? 2 : 1,
        visning: form.visning,
      });

      messages.setSuccessMessage('PDF generert');
    } catch (err) {
      console.error('Kunne ikke generere PDF:', err);
      messages.setErrorMessage('Kunne ikke generere PDF');
    }
  };

  return (
    <div className="space-y-6">
      {messages.errorMessage && (
        <MessageToast
          type="error"
          message={messages.errorMessage}
          onDismiss={() => messages.setErrorMessage(null)}
        />
      )}
      {messages.successMessage && (
        <MessageToast
          type="success"
          message={messages.successMessage}
          onDismiss={() => messages.setSuccessMessage(null)}
        />
      )}

      <TranspositionToolCard
        transposeBy={form.transposeBy}
        onTransposeByChange={form.setTransposeBy}
        toneart={toneart}
        erTallsang={form.erTallsang}
        visning={form.visning}
        onVisningChange={form.setVisning}
        saving={form.saving}
        onSave={form.handleSaveTransposition}
        settingsOpen={settings.settingsOpen}
        onSettingsOpenChange={settings.setSettingsOpen}
        fontSize={settings.fontSize}
        onFontSizeChange={settings.setFontSize}
        chordFontSize={settings.chordFontSize}
        onChordFontSizeChange={settings.setChordFontSize}
        chordColor={settings.chordColor}
        onChordColorChange={settings.setChordColor}
        columns={settings.columns}
        onColumnsChange={settings.setColumns}
        tekstOgAkkorder={tekstOgAkkorder}
        linjer={linjer}
        hode={hode}
        onDownloadPDF={handleDownloadPDF}
      />

      <SavedTranspositionsCard
        savedTranspositions={transpositions.savedTranspositions}
        grunntone={grunntone}
        toneartType={toneartType}
        loading={transpositions.loading}
        onLoad={form.lastInn}
        onDelete={handleDeleteTransposition}
      />
    </div>
  );
}

